import {
  aytyErrorLabel,
  dayFormat,
  formatDistanceLocale,
  fullTimeFormat,
  oneSecond,
  unauthorizedCode,
} from "consts";
import { format } from "date-fns";
import { BaseReponseType } from "models";

export const addTimeSuffix = (date: string, isEnd?: boolean) => {
  const suffix = isEnd ? "23:59:59" : "00:00:00";
  return `${format(
    new Date(date.length > 10 ? date : `${date} 12:00`),
    dayFormat,
  )} ${suffix}`;
};

export const aytyFormatError = <T extends Omit<BaseReponseType, "dtServer">>(
  data: T,
) => {
  if (data.idReturnAPI === unauthorizedCode) return "errors.loggedOutUser";
  else return data.deReturnAPI.replace(aytyErrorLabel, "").trim();
};

export const clearObject = (obj: Object, clearEmptyString?: boolean) => {
  return Object.assign(
    {},
    ...Object.entries(obj).map(([key, value]) => {
      if (
        (clearEmptyString && value === "") ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      )
        return undefined;
      return { [key]: value };
    }),
  );
};

export const extractFileContent = async (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    try {
      reader.onload = async ({ target }) =>
        resolve(target?.result?.toString() || "");
    } catch (error) {
      return reject(new Error("Erro ao extrair texto do arquivo"));
    }
    reader.readAsText(file);
  });
};

export const formatAgentName = (name = "", ignoreLastName = false) => {
  const names = name.trim().split(" ");
  if (names.length > 1 && !ignoreLastName)
    return `${names[0]} ${names[names.length - 1]}`;
  return names[0];
};

export const formatDistance = (token: any, count: any, options?: any) => {
  const result = formatDistanceLocale[token].replace("{{count}}", count);

  if (!options.addSuffix) return result;

  if (options.comparison > 0) return "in " + result;
  else return result + " ago";
};

export const formatFileSize = (bytes: number) => {
  const thresh = 1024;

  if (Math.abs(bytes) < thresh) return bytes + " B";

  const units = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  let u = -1;
  const r = 10;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(1) + " " + units[u];
};

export const formatText = (text: string) => {
  return text.replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, "\n");
};

export const milisecondsToTimeString = (time?: number) => {
  if (!time) return "";

  const [seconds, miliseconds] = (time / oneSecond).toString().split(".");

  if (seconds !== "0") return `${seconds}s ${miliseconds}ms`;

  return `${miliseconds}ms`;
};

export const objectToFormData = (obj: Object) => {
  const formData = new FormData();
  Object.entries(obj).map(([key, value]) => formData.append(key, value));
  return formData;
};

export const objectToURLParams = (obj: Object) => {
  return Object.entries(obj)
    .map(
      ([key, value]) =>
        `${key}=${
          Array.isArray(value)
            ? value.map((v) => encodeURIComponent(v)).join(",")
            : encodeURIComponent(value)
        }`,
    )
    .filter((item) => !!item)
    .join("&");
};

export const secondsToTimeString = (
  seconds?: number,
  path = fullTimeFormat,
  toClean?: boolean,
) => {
  if (!seconds) return "";

  const date = new Date("01-01-0001");
  date.setSeconds(seconds);

  const res = format(date, path);

  return toClean ? res.replaceAll(/00{1}./gm, "").trim() : res;
};

export const splitDate = (text?: string) => {
  if (!text) return "--:--";
  return text.split(" ");
};

export const timeToSeconds = (time = "") => {
  const [hour = "", min = "", sec = ""] = time.split(":");
  return Number(hour) * 60 * 60 + Number(min) * 60 + Number(sec);
};

export const toBase64 = (text: string) =>
  window.btoa(unescape(encodeURIComponent(text)));

export const transformDynamicData = (dynamicData?: string) => {
  const fields = (dynamicData ?? "").split("|");
  return Object.assign(
    {},
    ...fields.map((field) => {
      if (!field) return {};

      const [key, value] = field.split("=");
      return { [key]: value };
    }),
  );
};
